// IndexedDB utility for persisting upload form data across OAuth redirects

import dayjs, { Dayjs } from 'dayjs';

const DB_NAME = 'naptune-uploads';
const STORE_NAME = 'pending-uploads';
const DB_VERSION = 1;

interface UploadContainerData {
  id: number;
  title: string;
  description: string;
  tags: string[];
  category: string;
  playlistId: string | null;
  privacyStatus: string;
  license: string;
  madeForKids: string;
  image: File | null;
  audio: File | null;
  uploadDate: Dayjs | null;
  isScheduled: boolean;
  imageResize?: {
    enabled: boolean;
    horizontal_offset?: number;
    vertical_offset?: number;
  } | null;
  disableWatermark?: string;
}

interface StoredUploadData {
  id: string;
  containers: Array<{
    id: number;
    title: string;
    description: string;
    tags: string[];
    category: string;
    playlistId: string | null;
    privacyStatus: string;
    license: string;
    madeForKids: string;
    imageData: string | null;  // base64
    imageName: string | null;
    imageType: string | null;
    audioData: string | null;  // base64
    audioName: string | null;
    audioType: string | null;
    uploadDate: string | null;
    isScheduled: boolean;
    imageResize: any;
    disableWatermark: string | undefined;
  }>;
  timestamp: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

export async function saveUploadData(containers: UploadContainerData[]): Promise<void> {
  const db = await openDB();

  // Convert Files to base64
  const serializedContainers = await Promise.all(
    containers.map(async (c) => ({
      id: c.id,
      title: c.title,
      description: c.description,
      tags: c.tags,
      category: c.category,
      playlistId: c.playlistId,
      privacyStatus: c.privacyStatus,
      license: c.license,
      madeForKids: c.madeForKids,
      imageData: c.image ? await fileToBase64(c.image) : null,
      imageName: c.image?.name || null,
      imageType: c.image?.type || null,
      audioData: c.audio ? await fileToBase64(c.audio) : null,
      audioName: c.audio?.name || null,
      audioType: c.audio?.type || null,
      uploadDate: c.uploadDate?.toISOString() || null,
      isScheduled: c.isScheduled,
      imageResize: c.imageResize,
      disableWatermark: c.disableWatermark,
    }))
  );

  const data: StoredUploadData = {
    id: 'pending',
    containers: serializedContainers,
    timestamp: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(data);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function loadUploadData(): Promise<UploadContainerData[] | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get('pending');

    request.onsuccess = async () => {
      const data = request.result as StoredUploadData | undefined;
      if (!data) {
        resolve(null);
        return;
      }

      // Check if data is stale (older than 1 hour)
      if (Date.now() - data.timestamp > 3600000) {
        await clearUploadData();
        resolve(null);
        return;
      }

      // Convert base64 back to Files
      const containers = await Promise.all(
        data.containers.map(async (c) => ({
          id: c.id,
          title: c.title,
          description: c.description,
          tags: c.tags,
          category: c.category,
          playlistId: c.playlistId,
          privacyStatus: c.privacyStatus,
          license: c.license,
          madeForKids: c.madeForKids,
          image: c.imageData ? base64ToFile(c.imageData, c.imageName!, c.imageType!) : null,
          audio: c.audioData ? base64ToFile(c.audioData, c.audioName!, c.audioType!) : null,
          uploadDate: c.uploadDate ? dayjs(c.uploadDate) : null,
          isScheduled: c.isScheduled,
          imageResize: c.imageResize,
          disableWatermark: c.disableWatermark,
        }))
      );

      resolve(containers as UploadContainerData[]);
    };

    request.onerror = () => reject(request.error);
  });
}

export async function clearUploadData(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete('pending');
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function base64ToFile(base64: string, name: string, type: string): File {
  const arr = base64.split(',');
  const bstr = atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new File([u8arr], name, { type });
}
